import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormInput from '@ui/forms/Input/FormInput'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import FormSelect from '@ui/forms/Select/FormSelect'
import InputsSection from '@ui/forms/InputsSection'
import HelpText from '@ui/elements/HelpText'
import FormSection, { FormRow } from './FormSection'
import { getCountryOptions } from '../countries'
import {
  CofidisMonthlyApplicationFormValues,
  CofidisSectionProps,
} from '../types'
import {
  FamilySituationValues,
  FamilySituation,
  IdTypeValues,
  SustainableSupport,
  SustainableSupportValues,
  TitleValues,
  Titles,
} from '../enums'

export default function IdentitySection({ fieldSuccess }: CofidisSectionProps) {
  const { t, i18n } = useTranslation(['cofidis'])
  const { control, trigger } =
    useFormContext<CofidisMonthlyApplicationFormValues>()
  const idType = useWatch({ control, name: 'idType' })
  const sustainableSupport = useWatch({ control, name: 'sustainableSupport' })

  const titleOptions = useMemo(
    () =>
      TitleValues.map((option) => ({
        value: option,
        label: t(`cofidis:title.options.${Titles[option]}.label`),
      })),
    [t]
  )
  const familySituationOptions = useMemo(
    () =>
      FamilySituationValues.map((option) => ({
        value: option,
        label: t(
          `cofidis:familySituation.options.${FamilySituation[option]}.label`
        ),
      })),
    [t]
  )
  const idTypeOptions = useMemo(
    () =>
      IdTypeValues.map((option) => ({
        value: option,
        label: t(`cofidis:idType.options.${option.toLowerCase()}.label`),
      })),
    [t]
  )
  const sustainableSupportOptions = useMemo(
    () =>
      SustainableSupportValues.map((option) => ({
        value: option,
        label: t(
          `cofidis:sustainableSupport.options.${SustainableSupport[option]}.label`
        ),
      })),
    [t]
  )
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.resolvedLanguage ?? i18n.language ?? 'en'),
    [i18n.resolvedLanguage, i18n.language]
  )

  useEffect(() => {
    if (idType) {
      trigger('idNumber')
    }
  }, [idType, trigger])

  return (
    <View className="flex flex-col gap-8">
      <InputsSection group>
        <FormSection>
          <FormRow>
            <FormSelect
              required
              labelVariant="top"
              control={control}
              name="title"
              isSuccess={fieldSuccess?.title}
              label={t('cofidis:title.label')}
              options={titleOptions}
            />
          </FormRow>
          <FormRow>
            <FormInput
              required
              labelVariant="top"
              name="firstName"
              isSuccess={fieldSuccess?.firstName}
              label={t('cofidis:firstName.label')}
              autoCapitalize="words"
              next="lastName1"
            />
            <FormInput
              required
              labelVariant="top"
              name="lastName1"
              isSuccess={fieldSuccess?.lastName1}
              label={t('cofidis:lastName1.label')}
              autoCapitalize="words"
              next="lastName2"
            />
            <FormInput
              required
              labelVariant="top"
              name="lastName2"
              isSuccess={fieldSuccess?.lastName2}
              label={t('cofidis:lastName2.label')}
              autoCapitalize="words"
              next="idType"
            />
          </FormRow>
          <FormRow>
            <FormSelect
              required={true}
              labelVariant="top"
              control={control}
              name="idType"
              isSuccess={fieldSuccess?.idType}
              label={t('cofidis:idType.label')}
              options={idTypeOptions}
            />
            <FormInput
              required
              labelVariant="top"
              name="idNumber"
              isSuccess={fieldSuccess?.idNumber}
              label={
                idType
                  ? t(`cofidis:idNumber.${idType}.label`)
                  : t('cofidis:idNumber.label')
              }
              placeholder={
                idType ? t(`cofidis:idNumber.${idType}.placeholder`) : undefined
              }
              autoCapitalize="characters"
              next="idCessationOfValidityDate"
            />
            <FormInputDate
              required
              labelVariant="top"
              name="idCessationOfValidityDate"
              isSuccess={fieldSuccess?.idCessationOfValidityDate}
              label={t('cofidis:idExpirationDate.label')}
            />
          </FormRow>
          <FormRow>
            <FormSelect
              required
              labelVariant="top"
              control={control}
              name="nationality"
              isSuccess={fieldSuccess?.nationality}
              label={t('cofidis:nationality.label')}
              options={countryOptions}
            />
          </FormRow>
          <FormRow>
            <FormInputDate
              required
              labelVariant="top"
              name="birthDate"
              isSuccess={fieldSuccess?.birthDate}
              label={t('cofidis:birthDate.label')}
            />
            <FormInput
              required={false}
              labelVariant="top"
              name="birthTown"
              isSuccess={fieldSuccess?.birthTown}
              label={t('cofidis:birthTown.label')}
              autoCapitalize="words"
              next="familySituation"
            />
            <FormSelect
              required
              labelVariant="top"
              control={control}
              name="birthCountry"
              isSuccess={fieldSuccess?.birthCountry}
              label={t('cofidis:birthCountry.label')}
              options={countryOptions}
            />
          </FormRow>
        </FormSection>
      </InputsSection>

      <InputsSection
        group
        divider
        title={t('cofidis:sections.family.header.title')}
        desc={t('cofidis:sections.family.header.desc')}
      >
        <FormSection>
          <FormRow>
            <FormSelect
              required
              labelVariant="top"
              control={control}
              name="familySituation"
              isSuccess={fieldSuccess?.familySituation}
              label={t('cofidis:familySituation.label')}
              options={familySituationOptions}
            />
          </FormRow>
          <FormRow>
            <FormInput
              required
              labelVariant="top"
              name="childSupportNumber"
              isSuccess={fieldSuccess?.childSupportNumber}
              label={t('cofidis:childSupportNumber.label')}
              inputMode="numeric"
              next="familyExpenses"
            />
            <FormInput
              labelVariant="top"
              control={control}
              name="familyExpenses"
              isSuccess={fieldSuccess?.familyExpenses}
              label={t('cofidis:familyExpenses.label')}
              inputMode="decimal"
              next="email"
              textEnd={t('cofidis:familyExpenses.end')}
            />
          </FormRow>
        </FormSection>
      </InputsSection>

      <InputsSection
        group
        divider
        title={t('cofidis:sections.contact.header.title')}
        desc={t('cofidis:sections.contact.header.desc')}
      >
        <FormSection>
          <FormRow>
            <View className="gap-8">
              <FormInput
                required
                labelVariant="top"
                name="email"
                isSuccess={fieldSuccess?.email}
                label={t('cofidis:email.label')}
                inputMode="email"
                autoCapitalize="none"
                next="phoneNumber"
              />
              <View>
                <FormSelect
                  required
                  labelVariant="top"
                  control={control}
                  name="sustainableSupport"
                  isSuccess={fieldSuccess?.sustainableSupport}
                  label={t('cofidis:sustainableSupport.label')}
                  options={sustainableSupportOptions}
                />
                {sustainableSupport === 'E' && (
                  <HelpText
                    text={t('cofidis:sustainableSupport.options.electronic.help')}
                  />
                )}
              </View>
            </View>
            <FormInput
              required
              labelVariant="top"
              name="phoneNumber"
              isSuccess={fieldSuccess?.phoneNumber}
              label={t('cofidis:phoneNumber.label')}
              inputMode="tel"
            />
          </FormRow>
          {/* 
          <FormInput
            required
            labelVariant="top"
            name="phoneNumber2"
            isSuccess={fieldSuccess?.phoneNumber2}
            label={t('cofidis:phoneNumber2.label')}
            inputMode="tel"
          />
          */}
        </FormSection>
      </InputsSection>
    </View>
  )
}
